import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import Card from "../../../../../components/atoms/Card.atom"
import { getAllProjects } from "../../../../../services/project.service"
import { getTeamMembers } from "../../../../../services/teamMember.service"
import CustomSortOverlay from "../../../components/CustomSortOverlay.compound"
import LabelWithQuantityBadge from "../../../components/LabelWithQuantityBadge.molecule"
import { getUserPendingTasksWithCustomSort } from "../../../../../services/pendingTask.service"
import MyPendingTasksDataTable from "../../../components/MyPendingTasksDataTable.compound"
import {
  LOCAL_STORAGE_CUSTOM_TASK_ORDER,
  LOCAL_STORAGE_SORT_CONFIG,
  MY_TASKS_DEFAULT_SORT_CONFIG,
  PENDING_TASK_FETCH_DATA_INTERVAL,
  SORT_OVERLAY_VALUES,
} from "../../../../../consts/modules/pendingTasks.consts"
import { useLocalStorage } from "../../../../../hooks/useLocalStorage.hook"
import { useSession } from "../../../../../hooks/auth/useSession.hook"
import {
  Accordion,
  AccordionTab,
} from "../../../../../components/atoms/Accordion.atom"
import { clsx } from "../../../../../utils/strings.utils"

const PendingTaskList = ({
  refresh,
  setTodoTasksLength,
  lastFinishChangeTaskID,
  setLastFinishChangeTaskID,
  taskIsBeingHidden,
  setTaskIsBeingHidden,
  triggerRefresh,
}) => {
  const [loading, setLoading] = useState(true)
  const [finishedTasks, setFinishedTasks] = useState([])
  const [todoTasks, setTodoTasks] = useState([])
  const [activeProjects, setActiveProjects] = useState([])
  const [activeTeamMembers, setActiveTeamMembers] = useState([])
  const [prevSortConfig, setPrevSortConfig] = useState({})
  const [sortConfig, setSortConfig] = useLocalStorage(
    LOCAL_STORAGE_SORT_CONFIG,
    MY_TASKS_DEFAULT_SORT_CONFIG
  )
  const [todoTasksOrder, setTodoTasksOrder] = useLocalStorage(
    LOCAL_STORAGE_CUSTOM_TASK_ORDER,
    []
  )
  const { user } = useSession()

  useEffect(() => {
    const interval = setInterval(() => {
      setLastFinishChangeTaskID(null)
      fetchData()
    }, PENDING_TASK_FETCH_DATA_INTERVAL)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!_.isEqual(prevSortConfig, sortConfig)) {
      setPrevSortConfig(sortConfig)
      fetchData()
    }
  }, [sortConfig])

  useEffect(() => {
    fetchData()
  }, [refresh])

  const fetchData = async () => {
    setLoading(true)
    await Promise.all([
      gettingActiveProjectsData(),
      gettingActiveTeamMembersData(),
      gettingUserTasksWithCustomSort(),
    ])
    setTaskIsBeingHidden(false)
    setLoading(false)
  }

  const gettingActiveProjectsData = async () => {
    // const response = await getActiveProjects()
    const response = await getAllProjects()
    if (response.success) {
      setActiveProjects(response.result)
    }
  }

  const gettingActiveTeamMembersData = async () => {
    const response = await getTeamMembers()
    if (response.success) {
      const allTeamMembers = response.result
      const teamMemberExceptMe = allTeamMembers.filter(
        (tm) => user.id !== tm.id
      )
      setActiveTeamMembers(teamMemberExceptMe)
    }
  }
  const gettingUserTasksWithCustomSort = async () => {
    let sortConfigToPost = { ...sortConfig }
    if (sortConfig.field === SORT_OVERLAY_VALUES.CUSTOM) {
      sortConfigToPost = {
        ...MY_TASKS_DEFAULT_SORT_CONFIG,
      }
    }
    const response = await getUserPendingTasksWithCustomSort({
      label: sortConfigToPost.label,
      field: sortConfigToPost.field,
      order: sortConfigToPost.order,
    })
    if (response.success) {
      processTasks(response.result)
    }
  }

  const processTasks = (tasks) => {
    const combinedTasks = tasks.reduce(
      (acc, task) => {
        if (task.isFinished) {
          acc.finishedTasks.push(task)
        } else {
          acc.todoTasks.push(task)
        }
        return acc
      },
      { finishedTasks: [], todoTasks: [] }
    )
    if (
      sortConfig.field === SORT_OVERLAY_VALUES.CUSTOM &&
      todoTasksOrder.length > 0
    ) {
      combinedTasks.todoTasks = applyLocalStorageCustomSort(
        combinedTasks.todoTasks
      )
      updateTodoTasksOrder(combinedTasks.todoTasks)
    }
    setFinishedTasks(combinedTasks.finishedTasks)
    setTodoTasks(combinedTasks.todoTasks)
    setTodoTasksLength(combinedTasks.todoTasks.length)
  }

  const applyLocalStorageCustomSort = (tasks) => {
    const ordered = todoTasksOrder
      .map((order) => tasks.find((task) => task.id === order.id))
      .filter(Boolean)
    const unordered = tasks.filter(
      (task) => !todoTasksOrder.some((order) => order.id === task.id)
    )
    return [...ordered, ...unordered]
  }
  const updateTodoTasksOrder = (tasks) => {
    const taskOrder = tasks.map((task, index) => ({
      id: task.id,
      orderIndex: index,
    }))
    setTodoTasksOrder(taskOrder)
  }

  const handleTodoItemDrop = async (event) => {
    const newOrderedTasks = event.value
    setTodoTasks(newOrderedTasks)
    const newOrder = newOrderedTasks.map((task, index) => ({
      id: task.id,
      orderIndex: index,
    }))
    setTodoTasksOrder(newOrder)
  }

  const [expandedListsFitInCard, setExpandedListsFitInCard] = useState(false)
  const [expandedIndex, setExpandedIndex] = useState(0)

  function onResize() {
    const pendingTasksList = document.querySelector(
      ".pending-task-list-content"
    )
    const mainLayoutAppWrapper = document.querySelector(".layout-main-content")

    if (pendingTasksList && mainLayoutAppWrapper) {
      const mainLayoutHeight =
        mainLayoutAppWrapper.offsetHeight -
        Number(
          window
            .getComputedStyle(mainLayoutAppWrapper)
            .paddingBottom.split("px")[0]
        ) *
          2
      const containerHeight =
        mainLayoutHeight - pendingTasksList.getBoundingClientRect().y

      const [pendingAccordion, finishedAccordion] =
        pendingTasksList.querySelectorAll(".p-accordion-tab")
      if (!pendingAccordion || !finishedAccordion) {
        setExpandedListsFitInCard((prev) => {
          if (prev === false) return false
          return false
        })

        if (pendingAccordion) {
          setExpandedIndex(0)
        } else {
          setExpandedIndex(1)
        }

        return
      }

      const pendingAccHeader = pendingAccordion.querySelector(
        ".p-accordion-header"
      )

      if (!pendingAccHeader) return

      const finishedAccHeader = finishedAccordion.querySelector(
        ".p-accordion-header"
      )
      if (!finishedAccHeader) return

      const pendingContentHeight =
        todoTasks.length === 0 ? 107 : todoTasks.length * 77

      const pendingContainerHeight =
        pendingAccHeader.offsetHeight + pendingContentHeight

      const finishedContentHeight = finishedTasks.length * 77

      const finishedContainerHeight =
        finishedAccHeader.offsetHeight + finishedContentHeight

      const contentIsBiggerThanContainer =
        pendingContainerHeight > containerHeight ||
        finishedContainerHeight > containerHeight ||
        pendingContainerHeight + finishedContainerHeight > containerHeight

      if (!contentIsBiggerThanContainer) {
        setExpandedListsFitInCard(true)
      } else {
        setExpandedListsFitInCard((prev) => {
          if (prev === false) return false
          return false
        })
      }
    }
  }

  useEffect(() => {
    onResize()
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [todoTasks, finishedTasks])

  return (
    <Card className="pending-task-list-wrapper shadow-2 min-h-0 overflow-hidden">
      <Accordion
        activeIndex={expandedListsFitInCard ? [0, 1] : expandedIndex}
        onTabChange={(e) => {
          if (expandedListsFitInCard) return
          if (e.index === null) {
            if (expandedIndex === 0) {
              setExpandedIndex(1)
            } else {
              setExpandedIndex(0)
            }
          } else {
            setExpandedIndex(e.index)
          }
        }}
        multiple={expandedListsFitInCard}
        transitionOptions={{
          timeout: 500,
        }}
        className={clsx("pending-task-list-content")}
      >
        <AccordionTab
          header={
            <div className="flex justify-content-between align-items-center w-full">
              <LabelWithQuantityBadge
                labelTemplate="Por hacer"
                labelProps={{
                  className: `font-cairo text-xl clr-text font-semibold`,
                }}
                containerProps={{
                  style: {
                    gap: "8px",
                  },
                }}
                badgeProps={{
                  style: {
                    background: "var(--primary-color-highlight)",
                  },
                }}
                quantity={todoTasks.length}
              />
              <div className="custom-sort-trigger">
                <CustomSortOverlay
                  sortConfig={sortConfig}
                  setSortConfig={setSortConfig}
                  showCustomSort
                  disabled={todoTasks.length === 0}
                  afterClickToggler={(e) => {
                    e.stopPropagation()
                  }}
                />
              </div>
            </div>
          }
          headerClassName={clsx(
            expandedListsFitInCard && "disabled-accordion-header-toggler",
            "pending-accordion-header"
          )}
        >
          <MyPendingTasksDataTable
            tasks={todoTasks}
            activeProjects={activeProjects}
            activeTeamMembers={activeTeamMembers}
            lastFinishChangeTaskID={lastFinishChangeTaskID}
            setLastFinishChangeTaskID={setLastFinishChangeTaskID}
            taskIsBeingHidden={taskIsBeingHidden}
            setTaskIsBeingHidden={setTaskIsBeingHidden}
            showLoading
            loading={loading}
            sortConfig={sortConfig}
            triggerRefresh={triggerRefresh}
            handleItemDrop={handleTodoItemDrop}
            isReorderable
          />
        </AccordionTab>
        {(finishedTasks.length > 0 ||
          (taskIsBeingHidden && finishedTasks.length === 0)) && (
          <AccordionTab
            header={
              <LabelWithQuantityBadge
                labelTemplate="Finalizadas de hoy"
                labelProps={{
                  className: `font-cairo text-xl clr-text font-semibold`,
                }}
                containerProps={{
                  style: {
                    gap: "8px",
                  },
                }}
                badgeProps={{
                  style: {
                    background: "var(--secondary-color)",
                  },
                }}
                quantity={finishedTasks.length}
              />
            }
            headerClassName={clsx(
              expandedListsFitInCard && "pointer-events-none"
            )}
            className="finished-pending-task-list"
          >
            <MyPendingTasksDataTable
              tasks={finishedTasks}
              activeProjects={activeProjects}
              activeTeamMembers={activeTeamMembers}
              lastFinishChangeTaskID={lastFinishChangeTaskID}
              setLastFinishChangeTaskID={setLastFinishChangeTaskID}
              taskIsBeingHidden={taskIsBeingHidden}
              setTaskIsBeingHidden={setTaskIsBeingHidden}
              showLoading={false}
              isReorderable={false}
              showCustomSort={false}
              triggerRefresh={triggerRefresh}
            />
          </AccordionTab>
        )}
      </Accordion>
    </Card>
  )
}

export default PendingTaskList

PendingTaskList.propTypes = {
  refresh: PropTypes.bool,
  setTodoTasksLength: PropTypes.func,
  lastFinishChangeTaskID: PropTypes.number || null,
  setLastFinishChangeTaskID: PropTypes.func,
  taskIsBeingHidden: PropTypes.bool,
  setTaskIsBeingHidden: PropTypes.func,
  triggerRefresh: PropTypes.func,
}
